import { CreateCommunity } from '../components/CreateCommunity.js';
import { Badge, Button, Empty, Failed, Spinner } from '../components/primitives.js';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  liveFirst, toggleSelection, adhocHref, roomHref, communityHref, byOverlap, suggestedRoomName,
  formatViewers, rosterPreview, yourRooms, sortShelf, followedIn, inBatches, isShelfSort,
  MAX_SELECTION, YOUR_ROOMS_SHOWN, LIVE_SHOWN, SHELF_SORTS,
  type FollowedChannel, type RoomCard, type ShelfSort,
} from '../lib/frontPage.js';
import {
  hueOf, hueVar, roomOrder, markerLetter, CHANNEL_HUES, browserOrders,
} from '../lib/roomColors.js';
import { loginErrorMessage } from '../lib/loginError.js';
import { SiteFrame } from '../components/SiteFrame.jsx';

/**
 * The front door.
 *
 * SIGNED OUT it shows what is here — public rooms, openable without an
 * account, because a door that asks who you are before telling you whether
 * anyone is home is not a door.
 *
 * SIGNED IN it answers one question: what can I watch right now, with people?
 * The answer is your own follows who are live, as faces you can press. Picking
 * two or three is either a room you open immediately or a room you keep, and
 * the difference between those is one button rather than one workflow.
 *
 * THE ORDER IS THE ARGUMENT. Your rooms first, because the room you were in
 * last night is the likeliest reason to be here; then who is live, which is
 * the reason to make a new one; then everything that is listed, in whichever
 * order somebody finds useful. Each long list folds, so all three are on the
 * first screen instead of the first one pushing the others below it.
 *
 * It is drawn from the console's own vocabulary — `Empty`, `Failed`, `Badge`,
 * `Button` and the ink/brand tokens. It used to be hand-styled in raw zinc,
 * which is how it ended up as the one page in the product where a request
 * that failed and a list with nothing in it said the same thing.
 */

interface Viewer {
  readonly uid: string;
  readonly handle: string | null;
  readonly displayName: string | null;
  readonly avatarUrl: string | null;
}

/**
 * A request that came back, or one that did not.
 *
 * `null` for both was the bug: every list on this page then had one state for
 * "Twitch has nothing for you" and "Twitch did not answer", and it showed the
 * first wording for the second case — telling somebody nobody they follow is
 * live when the truth was that nothing had been asked successfully.
 */
type Fetched<T> = { readonly ok: true; readonly value: T } | { readonly ok: false };

async function getJson<T>(url: string): Promise<Fetched<T>> {
  try {
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) return { ok: false };
    return { ok: true, value: (await response.json()) as T };
  } catch {
    return { ok: false };
  }
}

/**
 * One column width for every card on the page, people and rooms alike.
 *
 * `auto-fill` rather than a fixed count: the width stays constant and the
 * NUMBER of columns changes with the viewport, so cards never stretch into
 * bars on a wide screen or crush on a narrow one. Shared by both grids
 * because a page whose sections each pick their own column width is read as
 * several pages stacked.
 */
const CARD_GRID = 'grid grid-cols-[repeat(auto-fill,minmax(16rem,1fr))] gap-2';

/**
 * A channel, as a line marker.
 *
 * One letter in a filled circle, the way a transit line is a letter in a
 * circle. It is not decoration standing in for an avatar — on a room card
 * there IS no avatar, and the markers answer the only question the card
 * raises: who am I walking in on.
 */
/**
 * A channel's colour, worn by their face rather than beside it.
 *
 * It used to be a coloured letter in its own circle, with the avatar next to
 * it — two round things per row, saying the same thing twice. The letter was
 * never the point: the COLOUR is what the room labels a channel with, and a
 * ring puts it on the thing somebody is already looking at.
 *
 * The letter survives as the fallback for a channel with no picture, which
 * is what it always was underneath.
 */
function Face(
  { login, hue, avatarUrl, size = 'sm' }:
  { login: string; hue: number | null; avatarUrl?: string | null; size?: 'sm' | 'xs' },
) {
  const px = size === 'sm' ? 30 : 20;
  // The ring is drawn with box-shadow rather than a border so it sits OUTSIDE
  // the image: a border would eat two pixels of a face that is only thirty
  // across.
  const ring = hue === null
    ? { boxShadow: '0 0 0 2px var(--color-ink-700)' }
    : { boxShadow: `0 0 0 2px ${hueVar(hue)}` };
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt=""
        width={px}
        height={px}
        className="shrink-0 rounded-full"
        style={{ width: px, height: px, ...ring }}
      />
    );
  }
  return (
    <span
      aria-hidden
      className={`grid shrink-0 place-items-center rounded-full font-extrabold ${
        size === 'sm' ? 'text-[12px]' : 'text-[9px]'
      } ${hue === null ? 'bg-ink-800 text-ink-400' : 'text-ink-950'}`}
      style={{
        width: px,
        height: px,
        ...(hue === null ? {} : { background: hueVar(hue) }),
      }}
    >
      {markerLetter(login)}
    </span>
  );
}

function Marker(
  { login, hue, size = 'sm' }:
  { login: string; hue: number | null; size?: 'sm' | 'xs' },
) {
  const box = size === 'sm' ? 'size-[23px] text-[11.5px]' : 'size-[17px] text-[9px]';
  return (
    <span
      aria-hidden
      className={`grid shrink-0 place-items-center rounded-full font-extrabold ${box} ${
        // A channel with no position in the room gets no colour rather than a
        // borrowed one — see `hueOf`.
        hue === null ? 'bg-ink-800 text-ink-400' : 'text-ink-950'
      }`}
      {...(hue === null ? {} : { style: { background: hueVar(hue) } })}
    >
      {markerLetter(login)}
    </span>
  );
}

export function Landing(): ReactNode {
  const [viewer, setViewer] = useState<Viewer | null>(null);
  const [follows, setFollows] = useState<readonly FollowedChannel[]>([]);
  const [communities, setCommunities] = useState<readonly RoomCard[]>([]);
  /** Rooms you run and rooms you have been in, as one list — see `yourRooms`. */
  const [yours, setYours] = useState<readonly RoomCard[]>([]);
  /** Rooms you own or help run, which stay on your list — see `forget`. */
  const [runs, setRuns] = useState<ReadonlySet<string>>(new Set());
  const [selection, setSelection] = useState<readonly string[]>([]);
  const [matching, setMatching] = useState<readonly RoomCard[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [creating, setCreating] = useState(false);
  const [followsFailed, setFollowsFailed] = useState(false);
  const [roomsFailed, setRoomsFailed] = useState(false);
  const [sort, setSort] = useState<ShelfSort>(savedSort);
  /** Who is in each room right now, keyed by room id. */
  const [presence, setPresence] = useState<ReadonlyMap<string, RoomPresence>>(new Map());
  /** How many of each community's roster are live, from the live sweep. */
  const [liveCounts, setLiveCounts] = useState<ReadonlyMap<string, number>>(new Map());
  /** Bumped by "Try again", which is the only thing that reloads this page. */
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => { setAttempt((n) => n + 1); }, []);

  // The console and the landing share one HTML shell, so the static <title>
  // says "tomo console" on both. Correct for the half of it that is.
  useEffect(() => { document.title = 'tomo'; }, []);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setFollowsFailed(false);
    setRoomsFailed(false);
    void (async () => {
      // A 401 is the ordinary signed-out answer, not an error — which is why
      // a failed session read means "nobody is signed in" here and does not
      // raise the banner the other two do.
      const session = await getJson<{ user?: Viewer }>('/v1/auth/session');
      if (cancelled) return;
      const user = session.ok ? session.value.user ?? null : null;
      setViewer(user);

      /*
       * Everything at once. They are independent questions and the follows
       * call walks several Helix pages, so awaiting it before asking for the
       * rooms would leave the rest of the page blank for as long as Twitch
       * takes.
       */
      const [rooms, followed, visited, owned] = await Promise.all([
        getJson<{ data: ApiRoom[] }>('/v1/communities'),
        user ? getJson<{ data: ApiFollow[] }>('/v1/me/follows') : null,
        user ? getJson<{ data: ApiRoom[] }>('/v1/me/rooms') : null,
        /*
         * Your own rooms, which is a different question from where you have
         * BEEN. `/v1/me/rooms` is a visit log, so a private room you made and
         * have not opened yet appeared nowhere on this page — the one room
         * guaranteed not to be findable any other way, since it is unlisted
         * by definition. This one is membership, and membership ignores
         * visibility.
         */
        user ? getJson<{ data: ApiRoom[] }>('/v1/rooms') : null,
      ]);
      if (cancelled) return;
      setRoomsFailed(!rooms.ok);
      setCommunities(rooms.ok ? rooms.value.data.map(toCard) : []);
      if (followed) {
        setFollowsFailed(!followed.ok);
        setFollows(followed.ok ? followed.value.data.map((f) => ({
          login: f.login,
          displayName: f.display_name,
          avatarUrl: f.avatar_url,
          live: f.live,
          gameName: f.game_name,
          viewerCount: f.viewer_count,
        })) : []);
      }
      const ownedCards = owned?.ok ? owned.value.data.map(toCard) : [];
      setRuns(new Set(ownedCards.map((r) => r.id)));
      setYours(yourRooms(
        ownedCards,
        visited?.ok ? visited.value.data.map(toCard) : [],
      ));
      setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [attempt]);

  /**
   * Rooms that already watch the people you picked.
   *
   * Asked of the server rather than filtered from `communities`, because the
   * answer includes rooms that are not in the browse list — a room is listed
   * or not, and "does it watch these three" is a different question from
   * "should it appear on a shelf".
   */
  // The VALUE, extracted so the dependency is something that can be compared.
  // Depending on the array itself would refetch on every render, because
  // `selection` is rebuilt by every toggle — the same shape of bug the rooms
  // client already hit once with an inline object.
  const selectionKey = selection.join(',');
  useEffect(() => {
    if (selectionKey.length === 0) { setMatching([]); return undefined; }
    let cancelled = false;
    void getJson<{ data: ApiRoom[] }>(
      `/v1/communities?watching=${encodeURIComponent(selectionKey)}`,
    ).then((body) => { if (!cancelled) setMatching(body.ok ? body.value.data.map(toCard) : []); });
    return () => { cancelled = true; };
  }, [selectionKey]);

  /*
   * Presence, asked for AFTER the rooms and separately from them.
   *
   * The page does not know which rooms it has until the listings answer, and
   * presence changes on a different cadence from the lists — so it is one
   * batched call keyed by the ids actually on screen, not a field on three
   * endpoints that would make every room listing pay for a presence read.
   *
   * The ids as a STRING, so the effect depends on something comparable. The
   * arrays are rebuilt every render and would refetch on each one — the same
   * shape of bug as the selection above, and as the eighteen requests to
   * /v1/rooms/567 the room client once made for one page load.
   */
  const presenceKey = useMemo(
    () => [...new Set([...yours, ...communities].map((r) => r.id))].sort().join(','),
    [yours, communities],
  );
  useEffect(() => {
    // Occupancy is behind a login, so a signed-out page does not ask. It
    // would be a 401 per load and a count nobody is allowed to see.
    if (viewer === null || presenceKey.length === 0) { setPresence(new Map()); return undefined; }
    let cancelled = false;
    void Promise.all(inBatches(presenceKey.split(',')).map((ids) => getJson<{ data: RoomPresence[] }>(
      `/v1/rooms/presence?rooms=${encodeURIComponent(ids.join(','))}`,
    ))).then((bodies) => {
      if (cancelled) return;
      setPresence(new Map(bodies.flatMap((b) => (b.ok ? b.value.data : []))
        .map((row) => [row.room_id, row])));
    });
    return () => { cancelled = true; };
  }, [presenceKey, viewer]);

  /*
   * LIVE COUNTS, FOR EVERYBODY, EVERY MINUTE.
   *
   * Unlike presence, who is streaming is public, so a signed-out page asks
   * too. It is read from the live sweep's snapshot (ADR 0051), which changes
   * once a minute — so the page asks again once a minute while it is being
   * looked at, and not at all while it is in a background tab.
   */
  useEffect(() => {
    if (presenceKey.length === 0) { setLiveCounts(new Map()); return undefined; }
    let cancelled = false;
    const load = (): void => {
      void Promise.all(inBatches(presenceKey.split(',')).map((ids) => getJson<{ data: RoomLiveCount[] }>(
        `/v1/rooms/live?rooms=${encodeURIComponent(ids.join(','))}`,
      ))).then((bodies) => {
        if (cancelled) return;
        const next = new Map<string, number>();
        for (const row of bodies.flatMap((b) => (b.ok ? b.value.data : []))) {
          // Unknown stays absent, so a card says nothing rather than "0 live".
          if (row.live !== null) next.set(row.room_id, row.live);
        }
        setLiveCounts(next);
      });
    };
    load();
    const timer = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 60_000);
    const onVisible = (): void => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [presenceKey]);

  const live = useMemo(() => liveFirst(follows), [follows]);
  const displayOf = useMemo(() => {
    const names = new Map(follows.map((f) => [f.login, f.displayName]));
    return (login: string): string => names.get(login) ?? login;
  }, [follows]);
  const withSelection = useMemo(
    () => byOverlap(matching, selection),
    [matching, selection],
  );
  /*
   * EVERY PUBLIC ROOM, including ones that are also in "Your rooms".
   *
   * The shelf used to drop anything shown above it, so a room was drawn once.
   * That made "Last visited" a sort with nothing to sort — every room you had
   * visited had been taken off the shelf — and it made the shelf's length
   * depend on how many of your rooms were folded away. A public room appearing
   * in both places is now correct: the top list is yours, and this one is
   * everything that is listed.
   */
  const lastSeen = useMemo(
    () => new Map(yours.flatMap((r) => (r.lastSeenAt === undefined ? [] : [[r.id, r.lastSeenAt] as const]))),
    [yours],
  );
  const shelf = useMemo(
    () => (viewer === null ? [...communities] : sortShelf(communities, sort, {
      follows,
      occupancy: (id) => presence.get(id)?.total ?? 0,
      lastSeen: (id) => lastSeen.get(id),
    })),
    [viewer, communities, sort, follows, presence, lastSeen],
  );
  const chooseSort = (next: ShelfSort): void => {
    setSort(next);
    try { window.localStorage.setItem(SORT_KEY, next); } catch { /* private window */ }
  };

  /*
   * TAKE A ROOM OFF YOUR LIST — one you only visited.
   *
   * A room you run is on the list because you run it, not because you went
   * there, so it has no x: forgetting the visit would leave it exactly where
   * it is, and a button that visibly does nothing is worse than none.
   *
   * Off the page first, then asked of the server, and put back if it says no.
   * No confirmation, because nothing is lost: opening the room again puts it
   * back on the list.
   */
  const forget = (room: RoomCard): void => {
    setYours((list) => list.filter((r) => r.id !== room.id));
    void fetch(`/v1/me/rooms/${encodeURIComponent(room.id)}`, {
      method: 'DELETE',
      credentials: 'same-origin',
    }).then((r) => { if (!r.ok) throw new Error(String(r.status)); }).catch(() => {
      setYours((list) => (list.some((r) => r.id === room.id) ? list : yourRooms([], [...list, room])));
    });
  };

  const signIn = (): void => {
    void fetch('/v1/auth/start', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ return_to: '/' }),
    })
      .then(async (r) => (r.ok ? ((await r.json()) as { authorize_url?: string }) : null))
      .then((body) => { if (body?.authorize_url) window.location.assign(body.authorize_url); })
      .catch(() => undefined);
  };

  const roomProps = { displayOf, presence, liveCounts };

  /*
   * A SIGN-IN THAT FAILED SAYS SO, HERE.
   *
   * The callback redirects to `/?login_error=…` and this is `/` — but the
   * only page reading that parameter was the console's sign-in screen, which
   * nobody lands on. So a failed login arrived as the front page, signed out,
   * with the reason unread in the address bar and nothing on screen to
   * explain why anything that needs an account had stopped working.
   */
  const loginError = loginErrorMessage(
    new URLSearchParams(window.location.search).get('login_error'),
  );

  return (
    <SiteFrame viewer={viewer} tagline="Watch with your friends.">
      <div className="flex flex-col gap-10">
      {loginError !== null && (
        <Failed
          message="That sign-in did not finish."
          detail={loginError}
          onRetry={signIn}
        />
      )}

      {/*
        YOURS FIRST. The rooms somebody already has are the likeliest reason
        they opened this page, and they are the shortest path back into one —
        so the three they used last sit above everything, and the rest wait
        behind a fold rather than pushing who is live off the first screen.
      */}
      {yours.length > 0 && (
        <Folded
          title="Your rooms"
          items={yours}
          shown={YOUR_ROOMS_SHOWN}
          render={(room) => (
            <RoomTile
              room={room}
              {...roomProps}
              {...(runs.has(room.id) ? {} : { onForget: () => { forget(room); } })}
            />
          )}
          keyOf={(room) => room.id}
        />
      )}

      {viewer ? (
        <LiveFollows
          live={live}
          loaded={loaded}
          failed={followsFailed}
          onRetry={retry}
          selection={selection}
          onToggle={(login) => { setSelection((s) => toggleSelection(s, login)); }}
        />
      ) : (
        <section className="border-l-4 border-brand-400 bg-ink-900 px-5 py-5">
          <h2 className="text-[15px] font-bold tracking-tight text-ink-50">Sign in to see who you follow</h2>
          <p className="mt-1.5 max-w-prose text-sm text-ink-400">
            tomo reads your Twitch follows to show which of them are live, so you
            can put two or three of them on one screen. Rooms below are open to
            anybody — no account needed to watch or read.
          </p>
          <div className="mt-4">
            <Button onClick={signIn}>Sign in with Twitch</Button>
          </div>
        </section>
      )}

      {/*
        Directly under the list it answers. It appears when somebody picks,
        so it belongs next to where they are picking, not under two more
        sections they would have to scroll past to notice it arrived.
      */}
      {withSelection.length > 0 && (
        <Section title="Rooms with these streamers">
          <ul className={CARD_GRID}>
            {withSelection.map(({ room, shared }) => (
              <li key={room.id}>
                <RoomTile
                  room={room}
                  {...roomProps}
                  // Says WHY this room is in the list. "Also watches x and y"
                  // is the whole reason it is being offered.
                  note={`Also watches ${shared.map(displayOf).join(', ')}`}
                />
              </li>
            ))}
          </ul>
        </Section>
      )}

      {selection.length > 0 && (
        <SelectionBar
          selection={selection}
          displayOf={displayOf}
          onClear={() => { setSelection([]); }}
        />
      )}

      {creating && (
        <CreateCommunity
          follows={follows}
          onClose={() => { setCreating(false); }}
          /*
            `/c/{slug}` — the thing somebody wants immediately after making a
            community is to be STANDING in it, not reading a directory of the
            one person they just added (ADR 0047).
          */
          onCreated={(slug) => { window.location.assign(`/c/${slug}`); }}
        />
      )}

      <Section
        title="Public rooms"
        count={shelf.length}
        aside={
          <span className="flex flex-wrap items-center gap-3">
            {/*
              Only signed in: every order but the server's is built from
              something only an account has — follows, visits, and occupancy,
              which is not public (see the presence route).
            */}
            {viewer !== null && shelf.length > 1 && (
              <SortTabs value={sort} onChange={chooseSort} />
            )}
            {/*
              Next to the rooms other people made, which is where somebody is
              standing when they decide they want one of their own. Only for
              people who are signed in — a community needs an owner.
            */}
            {viewer !== null && (
              <button
                type="button"
                onClick={() => { setCreating(true); }}
                className="shrink-0 border border-ink-700 px-3 py-1 text-xs text-ink-200 transition hover:border-brand-400 hover:text-brand-300"
              >
                Create a community
              </button>
            )}
          </span>
        }
      >
        {!loaded ? (
          <Spinner label="Looking for rooms…" />
        ) : roomsFailed ? (
          <Failed
            message="The room list did not load."
            detail="Nothing is wrong with your rooms — this page could not reach tomo."
            onRetry={retry}
          />
        ) : shelf.length === 0 ? (
          /*
            An empty list is a fact, not a failure, and it says what to do
            instead. "No communities yet" with nothing after it reads as a
            broken page.
          */
          <Empty
            title="No public rooms yet"
            hint="A room of your own is a link you can send someone — pick a few streamers above and make one."
            {...(viewer !== null
              ? { action: <Button onClick={() => { setCreating(true); }}>Create a community</Button> }
              : {})}
          />
        ) : (
          <ul className={CARD_GRID}>
            {shelf.map((room) => {
              /*
                In "For you" the card says WHY it is there, in place of the
                roster — the same move as "Also watches" above. A ranking
                nobody can see the reason for reads as an advert.
              */
              const why = viewer !== null && sort === 'recommended'
                ? recommendationNote(followedIn(room, follows), displayOf)
                : undefined;
              return (
                <li key={room.id}>
                  <RoomTile room={room} {...roomProps} {...(why !== undefined ? { note: why } : {})} />
                </li>
              );
            })}
          </ul>
        )}
      </Section>
      </div>
    </SiteFrame>
  );
}

/** Where the shelf's order is remembered — per browser, a convenience only. */
const SORT_KEY = 'tomo.front.sort';

function savedSort(): ShelfSort {
  try {
    const saved = window.localStorage.getItem(SORT_KEY);
    return isShelfSort(saved) ? saved : 'recommended';
  } catch {
    return 'recommended';
  }
}

function recommendationNote(
  { live, offline }: { readonly live: readonly string[]; readonly offline: readonly string[] },
  displayOf: (login: string) => string,
): string | undefined {
  if (live.length > 0) return `${live.map(displayOf).join(', ')} live now`;
  if (offline.length > 0) return `You follow ${offline.map(displayOf).join(', ')}`;
  return undefined;
}

/* ---------------------------------------------------------------- pieces */

/**
 * One heading, one rule, one thing under it.
 *
 * The count is next to the title rather than in the copy: four stacked lists
 * on a dark page need something that says how long each one is before you
 * start reading it.
 */
function Section(
  { title, count, aside, children }:
  { title: string; count?: number; aside?: ReactNode; children: ReactNode },
) {
  return (
    <section>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-3 text-[15px] font-bold tracking-tight text-ink-50">
          {title}
          {count !== undefined && count > 0 && (
            /* Solid, like a platform number. A dim grey digit beside a
               heading reads as an afterthought; this reads as a count. */
            <span className="bg-ink-50 px-[7px] py-px text-xs font-bold text-ink-950 tabular-nums">
              {count}
            </span>
          )}
        </h2>
        {aside}
      </div>
      <div className="mt-3">{children}</div>
    </section>
  );
}

/**
 * How many people are in a room, and who.
 *
 * A COUNT YOU CAN HOVER, not a row of faces. Faces on every card would make
 * the page a directory of people rather than of rooms, and most rooms most of
 * the time have nobody in them — a card that is mostly empty avatars reads as
 * broken. The count is the fact; the names are the follow-up question.
 *
 * `title` rather than a custom popover on purpose: it works on keyboard focus
 * and with a screen reader without any of this having to reimplement either,
 * and the names are exactly the kind of secondary detail a browser tooltip is
 * for. The count itself is always readable without hovering anything.
 *
 * Nothing at all when the room is empty. "0 here" is a fact nobody needs and
 * it would sit on almost every card.
 */
function Present({ presence }: { presence: RoomPresence | undefined }) {
  if (presence === undefined || presence.total === 0) return null;
  const names = presence.sample.map((p) => p.handle);
  const extra = presence.total - names.length;
  const listed = [...names, ...(extra > 0 ? [`and ${String(extra)} more`] : [])].join(', ');
  return (
    <span
      title={`In this room on tomo: ${listed}`}
      className="inline-flex shrink-0 items-center gap-1 text-[11px] text-ink-200 tabular-nums"
    >
      <span aria-hidden className="size-1.5 rounded-full bg-good-400" />
      {presence.total} watching
      <span className="sr-only"> on tomo: {listed}</span>
    </span>
  );
}

/**
 * How many of a community's streamers are live.
 *
 * NAMED, beside the count of who is watching, because both are small numbers
 * with a dot and they answer different questions: this is the streamers on
 * Twitch, that is the people in the room on tomo. A bare "1" next to a bare
 * "56" left anybody to guess which was which.
 *
 * Nothing when nobody is live, like `Present` — and nothing when the count is
 * unknown, which the page never turns into zero.
 */
function LiveCount({ live }: { live: number | undefined }) {
  if (live === undefined || live === 0) return null;
  return (
    <span
      title={`${String(live)} of this community's streamers are live on Twitch`}
      className="inline-flex shrink-0 items-center gap-1 text-[11px] text-ink-200 tabular-nums"
    >
      <span aria-hidden className="size-1.5 rounded-full bg-bad-400" />
      {live} live
    </span>
  );
}

function LiveFollows({
  live, loaded, failed, onRetry, selection, onToggle,
}: {
  live: readonly FollowedChannel[];
  loaded: boolean;
  failed: boolean;
  onRetry: () => void;
  selection: readonly string[];
  onToggle: (login: string) => void;
}) {
  const full = selection.length >= MAX_SELECTION;
  const [open, setOpen] = useState(false);
  /*
   * A PICK IS NEVER FOLDED AWAY. Somebody who opens the list, picks the
   * fourteenth channel and closes it again would otherwise have a selection
   * containing a face they can no longer see or un-press.
   */
  const shown = open
    ? live
    : live.filter((c, i) => i < LIVE_SHOWN || selection.includes(c.login));
  return (
    <Section
      title="Live now"
      count={live.length}
      aside={
        live.length > 0 ? (
          <p className="text-xs text-ink-400">
            Pick up to {MAX_SELECTION} — the first two open playing
          </p>
        ) : undefined
      }
    >
      {!loaded ? (
        <Spinner label="Asking Twitch who is on…" />
      ) : failed ? (
        /*
          The distinction this page did not used to make. Somebody whose
          follows failed to load was told nobody they follow is live, which
          is a different claim and a wrong one.
        */
        <Failed
          message="Twitch did not answer."
          detail="Your follows are what this page is built from, so it has nothing to show until it does."
          onRetry={onRetry}
        />
      ) : live.length === 0 ? (
        <Empty
          title="Nobody you follow is live right now"
          hint="The public rooms below are open whenever you are."
        />
      ) : (
        /*
         * PEOPLE ARE PILLS, ROOMS ARE TILES, and both sit on the same column
         * width. The shape says which kind of thing you are looking at
         * without a label having to; the shared width is what lets the whole
         * page be scanned down one left edge.
         *
         * Every card used to size itself to its own name and game, so no two
         * were alike and no two rows lined up — and a list of twenty is read
         * by scanning down the left edge, which there was not one of.
         */
        <>
        <ul className={CARD_GRID}>
          {shown.map((channel) => {
            const index = live.indexOf(channel);
            const picked = selection.includes(channel.login);
            const viewers = formatViewers(channel.viewerCount);
            /*
             * Positional, and only within this list.
             *
             * There is no room here yet, so there is no room order to honour
             * — these colours are a legend, not a promise. The moment a room
             * exists the colours come from IT (see `RoomTile`), which is the
             * only place a colour can claim to mean something.
             */
            const hue = (index % CHANNEL_HUES) + 1;
            return (
              <li key={channel.login}>
                <button
                  type="button"
                  onClick={() => { onToggle(channel.login); }}
                  // Disabled only when the list is full AND this one is not
                  // already in it, so deselecting always works.
                  disabled={full && !picked}
                  aria-pressed={picked}
                  className={`flex w-full items-center gap-2.5 px-3 py-2 text-left transition disabled:opacity-40 motion-reduce:transition-none ${
                    picked
                      ? 'bg-ink-200 text-ink-950'
                      : 'bg-ink-900 hover:bg-ink-800'
                  }`}
                >
                  {/*
                    ONE ROUND THING PER ROW, not two. The face wears the
                    room's colour as a ring; the letter only appears when
                    there is no face to put it on. A wall of twelve letters
                    was a puzzle, and a letter beside a face was the same
                    thing said twice.
                  */}
                  <Face login={channel.login} hue={hue} avatarUrl={channel.avatarUrl} />
                  <span className="min-w-0 flex-1">
                    <span className={`block truncate text-[13.5px] leading-[1.2] font-semibold ${
                      picked ? 'text-ink-950' : 'text-ink-50'
                    }`}>
                      {channel.displayName}
                    </span>
                    <span className={`block truncate text-[11px] leading-[1.35] ${
                      picked ? 'text-ink-800' : 'text-ink-400'
                    }`}>
                      {/* The game if there is one, then the plain fact. Never blank. */}
                      {channel.gameName || 'live'}
                    </span>
                  </span>
                  {viewers !== '' && (
                    /*
                      Right-aligned, in its own column, because the list is
                      SORTED by this number — smallest audience first — and a
                      sort nobody can see reads as a shuffle.
                    */
                    <span className={`shrink-0 text-[11px] tabular-nums ${
                      picked ? 'text-ink-800' : 'text-ink-700'
                    }`}>
                      {viewers}
                      <span className="sr-only"> viewers</span>
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
        <MoreToggle total={live.length} shown={shown.length} open={open} onToggle={setOpen} />
        </>
      )}
    </Section>
  );
}

/**
 * A section whose list shows its first few and folds the rest.
 */
function Folded<T>({
  title, items, shown, render, keyOf,
}: {
  title: string;
  items: readonly T[];
  shown: number;
  render: (item: T) => ReactNode;
  keyOf: (item: T) => string;
}) {
  const [open, setOpen] = useState(false);
  const visible = open ? items : items.slice(0, shown);
  return (
    <Section title={title} count={items.length}>
      <ul className={CARD_GRID}>
        {visible.map((item) => <li key={keyOf(item)}>{render(item)}</li>)}
      </ul>
      <MoreToggle total={items.length} shown={visible.length} open={open} onToggle={setOpen} />
    </Section>
  );
}

/**
 * The fold, as one full-width bar under the grid.
 *
 * Full width and under the list rather than a link by the heading: it is the
 * END of what is showing, so it goes where the list stops, and it says how
 * many are behind it — "Show all" over an unknown number is a gamble.
 * Nothing at all when nothing is folded.
 */
function MoreToggle({
  total, shown, open, onToggle,
}: {
  total: number;
  shown: number;
  open: boolean;
  onToggle: (open: boolean) => void;
}) {
  if (!open && shown >= total) return null;
  return (
    <button
      type="button"
      aria-expanded={open}
      onClick={() => { onToggle(!open); }}
      className="mt-2 flex w-full items-center justify-center gap-2 border border-ink-800 py-1.5 text-xs font-semibold text-ink-400 transition hover:border-ink-700 hover:text-ink-50"
    >
      {open ? 'Show fewer' : `Show all ${String(total)}`}
      {!open && (
        <span className="font-normal text-ink-700 tabular-nums">+{total - shown}</span>
      )}
    </button>
  );
}

/**
 * How the public shelf is ordered.
 *
 * Pressed tabs rather than a <select>: three choices fit, and a dropdown
 * hides the other two orders from somebody who does not know they exist.
 * The same pressed style as the appearance switch in the account menu.
 */
function SortTabs({ value, onChange }: { value: ShelfSort; onChange: (next: ShelfSort) => void }) {
  return (
    <span role="group" aria-label="Order public rooms by" className="flex border border-ink-800">
      {SHELF_SORTS.map(({ key, label }) => (
        <button
          key={key}
          type="button"
          aria-pressed={value === key}
          onClick={() => { onChange(key); }}
          className={`px-2.5 py-1 text-xs transition ${
            value === key
              ? 'bg-ink-50 font-semibold text-ink-950'
              : 'text-ink-400 hover:bg-ink-800 hover:text-ink-50'
          }`}
        >
          {label}
        </button>
      ))}
    </span>
  );
}

/**
 * What to do with what you picked.
 *
 * TWO BUTTONS, and the distinction is the product. "Watch now" is an address —
 * nothing is created, nothing is listed, and the link is shareable by being
 * copied. "Keep as a room" makes a thing with a name that other people can
 * find, which is a decision and so has a visibility with it.
 *
 * It STICKS to the bottom of the viewport once you scroll past it. A live
 * list can run to forty channels, and a selection bar that scrolls away takes
 * both of those buttons with it — you pick four people and then have to find
 * your way back up the page to do anything with them.
 */
function SelectionBar({
  selection, displayOf, onClear,
}: {
  selection: readonly string[];
  displayOf: (login: string) => string;
  onClear: () => void;
}) {
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const [visibility, setVisibility] = useState<'public' | 'private'>('public');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const suggestion = suggestedRoomName(selection, displayOf);
  const href = adhocHref(selection);

  const create = (): void => {
    setBusy(true);
    setError(null);
    void fetch('/v1/rooms', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        kind: 'community',
        visibility,
        // The suggestion when the box is untouched, so the button is never
        // dead on an empty field.
        name: name.trim() || suggestion,
        twitch_logins: selection,
      }),
    })
      .then(async (response) => {
        if (!response.ok) {
          setBusy(false);
          /*
           * The one failure a person can act on gets its own sentence. The
           * API answers 409 for a handle somebody already holds, and "could
           * not make that room" for that case sends them to try the same
           * name again.
           */
          setError(response.status === 409
            ? 'A room already has that name. Try another.'
            : 'Could not make that room.');
          return;
        }
        const room = (await response.json()) as { id: string; slug?: string | null };
        // A community, so the community path (ADR 0046).
        window.location.assign(`/c/${room.slug ?? room.id}`);
      })
      .catch(() => { setBusy(false); setError('Could not reach tomo.'); });
  };

  return (
    <section className="sticky bottom-4 z-10 rounded-lg border border-brand-400/40 bg-ink-900/95 px-4 py-3 shadow-lg shadow-black/40 backdrop-blur">
      <div className="flex flex-wrap items-center gap-3">
        <p className="min-w-0 flex-1 truncate text-sm">
          <span className="font-medium text-ink-50 tabular-nums">
            {selection.length} selected
          </span>
          <span className="ml-2 text-ink-400">
            {selection.map(displayOf).join(', ')}
          </span>
        </p>
        <button
          type="button"
          onClick={onClear}
          className="rounded px-2 py-1 text-xs text-ink-400 transition hover:text-ink-50"
        >
          Clear
        </button>
        <Button variant="ghost" onClick={() => { setNaming((v) => !v); }}>
          Keep as a room
        </Button>
        {href !== null && (
          <a
            href={href}
            className="rounded-md bg-brand-500 px-3 py-2 text-sm font-medium text-white transition hover:bg-brand-400"
          >
            Watch now
          </a>
        )}
      </div>

      {naming && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-ink-800 pt-3">
          <input
            value={name}
            onChange={(e) => { setName(e.target.value); }}
            placeholder={suggestion}
            aria-label="Room name"
            className="min-w-0 flex-1 rounded-md border border-ink-700 bg-ink-950 px-3 py-2 text-sm text-ink-50 placeholder:text-ink-700"
          />
          <select
            value={visibility}
            onChange={(e) => { setVisibility(e.target.value === 'private' ? 'private' : 'public'); }}
            aria-label="Who can find this room"
            className="rounded-md border border-ink-700 bg-ink-950 px-2 py-2 text-sm text-ink-200"
          >
            <option value="public">Listed publicly</option>
            <option value="private">Unlisted — link only</option>
          </select>
          <Button onClick={create} disabled={busy}>{busy ? 'Making…' : 'Make room'}</Button>
          {/*
            Says what "unlisted" actually means, because the other reading —
            that strangers are refused — is the natural one and is wrong.
            The link is the invite.
          */}
          <p className="w-full text-xs text-ink-400">
            {visibility === 'public'
              ? 'Anyone can find this room from this page.'
              : 'Not listed anywhere. Anyone you send the link to can open it.'}
          </p>
          {error !== null && <p className="w-full text-xs text-bad-400">{error}</p>}
        </div>
      )}
    </section>
  );
}

/**
 * A room, as something you can tell apart from the room under it.
 *
 * The roster is the identifying fact — two communities can share a name, and
 * "567" tells you nothing that "567, watching stefancoat and awful_tie" does
 * not tell you better. Faces where we have them, which is wherever this
 * viewer follows the person, because a face is read faster than a login.
 */
function RoomTile({
  room, note, displayOf, presence, liveCounts, onForget,
}: {
  room: RoomCard;
  note?: string;
  displayOf: (login: string) => string;
  presence: ReadonlyMap<string, RoomPresence>;
  liveCounts: ReadonlyMap<string, number>;
  /** Present only where the card may be taken off the list — see `Your rooms`. */
  onForget?: () => void;
}) {
  /*
   * THE ROOM'S OWN COLOURS, not this page's.
   *
   * A room assigns a hue by a channel's position in it, so the markers here
   * are the ones that will be on screen when the card is opened — the same
   * letters, the same colours, in the same order. Nothing is stored to make
   * that true; the same rule is applied to the same list.
   */
  const order = roomOrder({ id: room.id, channels: room.channels }, browserOrders);
  /*
   * The markers carry more than the names do, so the names get less room.
   *
   * Both lines say the same thing, and the line of text was the one losing —
   * four truncated logins ending in "fruitb…". Three names for people the
   * letters cannot identify on their own, and up to eight markers, which is
   * the most a room opens with anyway.
   */
  const { shown: named, extra } = rosterPreview(order, 3);
  const shown = order.slice(0, MAX_SELECTION);
  const roster = room.channels.length === 0
    // A feed-backed room lists nothing here and is not empty — its roster
    // comes from elsewhere (ADR 0043), so saying "no channels" would be wrong.
    ? 'Opens with whoever is live'
    : named.map(displayOf).join(', ');
  /*
   * A DESCRIPTION, WHEN THERE IS ONE, SAYS MORE THAN THREE NAMES. "Stefancoat,
   * FrostPrime, BrentotheDan" tells you who is in a room; the line its admins
   * wrote tells you what it is. The markers below still show who, so nothing
   * is lost. A recommendation's reason (`note`) still outranks both — it is
   * the answer to why this card is in front of you.
   */
  const line = note ?? room.description ?? roster;
  const showsRoster = note === undefined && room.description === undefined;

  /*
   * ONE CARD, THREE THINGS TO PRESS: the room, its community page, and (on
   * your own list) the x. Links cannot nest, so the card is not itself a link
   * any more — the name is, and its hit area is stretched over the whole card
   * with `after:inset-0`, so pressing anywhere still opens the room. The two
   * smaller controls sit above that layer.
   */
  return (
    <div className="relative h-full bg-ink-900 px-3 py-2.5 transition focus-within:bg-ink-800 hover:bg-ink-800">
      <div className="flex items-baseline gap-2">
        <a
          href={roomHref(room)}
          className="min-w-0 flex-1 truncate text-[13.5px] font-bold text-ink-50 outline-none after:absolute after:inset-0"
        >
          {room.name}
        </a>
        {/*
          "Unlisted", not "Private", and the same word the create field uses.
          On this page it is the useful half of the fact: a listed room is on
          the shelf below anyway, and an unlisted one is only ever reached
          from here or from a link you kept. `private` is the stored value;
          refusing strangers is what it does NOT mean.
        */}
        <LiveCount live={liveCounts.get(room.id)} />
        <Present presence={presence.get(room.id)} />
        {room.visibility === 'private' && <Badge>Unlisted</Badge>}
        {onForget !== undefined && (
          <button
            type="button"
            onClick={onForget}
            aria-label={`Remove ${room.name} from your rooms`}
            title="Remove from your rooms"
            className="relative z-10 -my-1 -mr-1.5 grid size-6 shrink-0 place-items-center rounded text-ink-700 transition hover:bg-ink-700 hover:text-ink-50"
          >
            <svg viewBox="0 0 12 12" aria-hidden className="size-2.5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
              <path d="M2.5 2.5l7 7M9.5 2.5l-7 7" />
            </svg>
          </button>
        )}
      </div>
      <div className="mt-1 flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-[11px] text-ink-400" title={line}>
          {line}
        </span>
        {/*
          The `+N` sits OUTSIDE the truncating span, because it was the first
          thing to be cut: three long names in a 16rem card ended at
          "GamesDoneQuick, BarbarousKin…", losing the only part of the line
          that said the room holds more than it is showing.
        */}
        {showsRoster && extra > 0 && (
          <span className="shrink-0 text-[11px] text-ink-700 tabular-nums">
            +{extra}
            <span className="sr-only"> more channels</span>
          </span>
        )}
      </div>
      {/*
        WHICH LINES STOP HERE. The one thing a room card could never say
        before: "567" and "test" name nothing, and two communities may share
        a name. These are the actual people, in the actual colours, and they
        are the same markers on the same channels inside.
      */}
      {(shown.length > 0 || room.kind === 'community') && (
        <div className="mt-2 flex items-center gap-1">
          {shown.map((login) => (
            <Marker key={login} login={login} hue={hueOf(order, login)} size="xs" />
          ))}
          {/*
            THE COMMUNITY'S OWN PAGE, beside the way into its room. The card
            opens the room — several streams and one chat — and this is the
            directory in front of it: who is in the community, who is on, a
            way to find one person among fifty. An ad-hoc room has no such
            page, so it gets no link.
          */}
          {room.kind === 'community' && (
            <a
              href={communityHref(room)}
              className="relative z-10 ml-auto text-[11px] text-ink-400 transition hover:text-brand-300"
            >
              Community page
            </a>
          )}
        </div>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------- wire */

interface ApiRoom {
  id: string;
  name: string;
  slug?: string | null;
  kind: string;
  visibility?: 'public' | 'private';
  description?: string;
  twitch_logins?: string[];
  /** The visit log's spelling of the same list — see `toCard`. */
  channels?: string[];
  last_seen_at?: string;
}

/** Matches the `RoomLiveCount` contract. `live` is null when it is unknown. */
interface RoomLiveCount {
  room_id: string;
  live: number | null;
  logins: string[];
}

/** Matches the `RoomPresence` contract; the page reads the wire shape. */
interface RoomPresence {
  room_id: string;
  total: number;
  sample: { handle: string; avatar_url: string | null }[];
}

interface ApiFollow {
  login: string;
  display_name: string;
  avatar_url: string | null;
  live: boolean;
  game_name: string;
  viewer_count: number;
}

function toCard(room: ApiRoom): RoomCard {
  return {
    id: room.id,
    name: room.name,
    slug: room.slug ?? null,
    kind: room.kind,
    /*
     * EITHER SPELLING. `/v1/me/rooms` names the roster `channels` where every
     * other listing says `twitch_logins`, and reading only the second gave
     * every room you had merely visited an empty roster — so it read "Opens
     * with whoever is live" under Your rooms while the same room listed its
     * streamers on the shelf below.
     */
    channels: room.twitch_logins ?? room.channels ?? [],
    ...(room.visibility !== undefined ? { visibility: room.visibility } : {}),
    // Blank is the admin page's way of removing it, so it counts as absent.
    ...(room.description?.trim() ? { description: room.description.trim() } : {}),
    ...(room.last_seen_at !== undefined ? { lastSeenAt: room.last_seen_at } : {}),
  };
}
